import React, { useMemo, useState } from 'react';
import { formatCents } from '@src/common';
import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import { useWidgetsTheme as theme } from '@src/core/theme';
import { CartItem } from '@src/core/buyerApiClient';
import { removeCartItem } from '@src/features/cart/store/cart';

import useTranslate from '@src/i18n/useTranslate';
import { useShowItemPrices } from '@src/hooks/useShowItemPrices';
import OutlinedButton from '@src/components/reusable/button/OutlinedButton';
import ErrorWarning from '../ErrorWarning';
import {
  AdditionalsContainer,
  AdditionalsProductName,
  AdditionalsProductValue,
  AdditionalsTitle,
  CartAdditionals,
  CartProduct,
  Container,
  InformationLabel,
  InformationValue,
  NameContainer,
  ProductImage,
  ProductInformation,
  ProductInformationContainer,
  ProductName,
  ProductProviderName,
  ResourceContainer,
  TariffContainer,
  TariffList,
} from './styles';
import TotalPriceSummary from './TotalPriceSummary';
import { calculateItemTotalPriceSummary } from '@src/domains/cart';
import { formatItemSchedule } from '@src/common/utils/formatItemSchedule';
import { appInfoSelector } from '@src/stores/app';
import { groupTariffsByType, getTariffTotalPrice } from '@src/domains/tariffs';
import {
  getSelectedLanguageValue,
  getLanguageOptionMeta,
  getNonLanguageSelectedAttributes,
  getAttributeTypeLabel,
} from '@src/domains/products';

interface CartItemProps {
  item: CartItem;
  editable?: boolean;
  showErrors?: boolean;
}

const CartItemContainer = (props: CartItemProps) => {
  const { item, editable = true, showErrors = true } = props;

  const [disabledItem, setDisabledItem] = useState(false);
  const { i18n } = useTranslate();
  const dispatch = useAppDispatch();
  const appInfo = useAppSelector(appInfoSelector);

  const [removeItemError, setRemoveItemError] = useState(false);

  const showItemPrices = useShowItemPrices();

  const removeItem = async () => {
    try {
      setDisabledItem(true);
      setRemoveItemError(false);
      await dispatch(removeCartItem(item));
    } catch (error) {
      setRemoveItemError(true);
    } finally {
      setDisabledItem(false);
    }
  };

  const mainColor = theme('actionColor');
  const tariffTotalValue = useMemo(() => getTariffTotalPrice(item.tariffs), [item.tariffs]);

  const tariffs = useMemo(() => groupTariffsByType(item.tariffs), [item.tariffs]);

  const { total, discount, previousPrice } = useMemo(() => calculateItemTotalPriceSummary(item), [item]);

  const selectedLanguageMeta = useMemo(() => {
    const selectedLanguageValue = getSelectedLanguageValue(item.selectedAttributes ?? []);

    return selectedLanguageValue ? getLanguageOptionMeta(selectedLanguageValue) : null;
  }, [item]);

  const otherSelectedAttributes = useMemo(
    () => getNonLanguageSelectedAttributes(item.selectedAttributes ?? []),
    [item],
  );

  return (
    <Container>
      <CartProduct>
        {item.product.thumbnailUrl ? (
          <ProductImage hidden={!item.product.thumbnailUrl}>
            <img src={item.product.thumbnailUrl ?? ''}></img>
          </ProductImage>
        ) : null}

        <ProductInformationContainer data-product-info={item.product.name}>
          <NameContainer>
            <ProductName data-product-name={item.product.name}>{item.product.name}</ProductName>
            {item.product.appId !== appInfo?.id ? (
              <ProductProviderName>
                {i18n('product_package_adder_sold_by', { owner: item.product.app.name })}
              </ProductProviderName>
            ) : null}
          </NameContainer>

          <ProductInformation>
            <InformationLabel>{i18n('cart_selectedDate')}:</InformationLabel>

            <InformationValue>
              <span>{formatItemSchedule(item, { fallbackText: i18n('account_sale_item_without_date') })}</span>
            </InformationValue>
          </ProductInformation>

          {selectedLanguageMeta ? (
            <ProductInformation>
              <InformationLabel>{i18n('cart_selectedLanguage')}:</InformationLabel>

              <InformationValue>
                <span>{selectedLanguageMeta.label}</span>
              </InformationValue>
            </ProductInformation>
          ) : null}

          {otherSelectedAttributes.map((attribute) => (
            <ProductInformation key={attribute.type}>
              <InformationLabel>{getAttributeTypeLabel(attribute.type, i18n)}:</InformationLabel>

              <InformationValue>
                <span>{attribute.value}</span>
              </InformationValue>
            </ProductInformation>
          ))}

          <TariffContainer>
            <InformationLabel>{i18n('cart_selectedTariff')}:</InformationLabel>

            <TariffList>
              {tariffs.map((cartTariff) => (
                <InformationValue key={cartTariff.tariff.id}>
                  {cartTariff.tariff.type.name} - {cartTariff.quantity}
                </InformationValue>
              ))}
            </TariffList>
          </TariffContainer>

          {item.resources.length > 0 && (
            <ResourceContainer>
              <InformationLabel>{i18n('cart_selectedResources')}:</InformationLabel>

              <TariffList>
                <InformationValue>{item.resources.map(({ resource: { name } }) => name).join(', ')}</InformationValue>
              </TariffList>
            </ResourceContainer>
          )}

          {tariffTotalValue && showItemPrices ? (
            <ProductInformation hidden={!tariffTotalValue}>
              <InformationLabel>{i18n('cart_value')}:</InformationLabel>

              <InformationValue>{formatCents(tariffTotalValue + item.costPreview.resourcePriceCents)}</InformationValue>
            </ProductInformation>
          ) : null}
        </ProductInformationContainer>
      </CartProduct>

      {item.additionals.length ? (
        <CartAdditionals hidden={!item.additionals.length}>
          <AdditionalsTitle>{i18n('cart_additionals')}</AdditionalsTitle>

          {item.additionals.map((cartAdditional) => (
            <AdditionalsContainer key={cartAdditional.additional.id}>
              <AdditionalsProductName>{cartAdditional.additional.title}</AdditionalsProductName>

              {showItemPrices ? (
                <AdditionalsProductValue>{formatCents(cartAdditional.amountCents)}</AdditionalsProductValue>
              ) : null}

              <AdditionalsProductValue>
                {i18n('cart_quantity')}: {cartAdditional.quantity}
              </AdditionalsProductValue>
            </AdditionalsContainer>
          ))}
        </CartAdditionals>
      ) : null}

      {showItemPrices ? <TotalPriceSummary previousPrice={previousPrice} total={total} discount={discount} /> : null}

      {showErrors && item.errors && item.errors.length > 0 ? (
        <ErrorWarning>{i18n('cart_itemErrorWarning')}</ErrorWarning>
      ) : null}

      {Number.isFinite(item.costPreview.productPackageOriginalDiscountCents) ? (
        <ErrorWarning variant='warning'>{i18n('discount_fallback_warning_message')}</ErrorWarning>
      ) : null}

      {editable ? (
        <OutlinedButton onClick={removeItem} color={mainColor} disabled={disabledItem} showLoadingOnDisabled>
          {i18n('cart_cancel')}
        </OutlinedButton>
      ) : null}

      {removeItemError ? <ErrorWarning>{i18n('cart_item_remove_error')}</ErrorWarning> : null}
    </Container>
  );
};

export default CartItemContainer;
